import { useCallback, useEffect, useRef, useState } from 'react';
import activeTicketService from '../../services/activeTicketsService';
import { WS_STATUS, useWebSocket } from '../../common/hooks/useWebSocket';
import { playChime } from '../../common/playChime';

export { WS_STATUS };

const QUEUE_TOPIC = '/topic/queue-events';
// Безусловный периодический ре-синк — страховка от «тихо умершего» WS
const RESYNC_INTERVAL_MS = 30_000;

export const useActiveTickets = () => {
	const [tickets, setTickets] = useState([]);
	const [loading, setLoading] = useState(false);
	const [fetched, setFetched] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	// ─── HTTP-загрузка (первичная + fallback-синхронизация) ───────────────────
	const fetchAll = useCallback(async () => {
		try {
			setLoading(true);
			const res = await activeTicketService.getAllGrades();
			setTickets(res.data || []);
			setErrorMessage('');
		} catch {
			setErrorMessage('Не удалось загрузить активные талоны');
		} finally {
			setLoading(false);
			setFetched(true);
		}
	}, []);

	// Загружаем при первом рендере
	useEffect(() => {
		fetchAll();
	}, [fetchAll]);

	// ─── Обработчик WebSocket-событий ────────────────────────────────────────
	const handleWsEvent = useCallback((event) => {
		// Поддержка нового формата { eventType, ticket } и старого { ticketNumber, ... }
		const eventType = event?.eventType;
		const ticket = event?.ticket ?? event; // старый бэк слал TicketDto напрямую

		switch (eventType) {
			case 'TICKET_GENERATED':
				// Новый талон в очереди → добавить в конец списка если ещё нет
				setTickets((prev) => {
					if (prev.some((t) => t.ticketNumber === ticket.ticketNumber))
						return prev;
					return [...prev, ticket];
				});
				break;

			case 'TICKET_CALLED':
				// Менеджер вызвал талон → обновить или добавить (с номером окна)
				setTickets((prev) => mergeTicket(prev, ticket));
				playChime();
				break;

			case 'TICKET_DONE':
				// Талон обслужен → удалить из активного списка
				setTickets((prev) =>
					prev.filter((t) => t.ticketNumber !== ticket.ticketNumber)
				);
				break;

			case 'QUEUE_RESET':
				// Ночной сброс → очистить всё
				setTickets([]);
				break;

			default:
				// Старый формат: бэк слал TicketDto без обёртки
				if (ticket?.ticketNumber) {
					setTickets((prev) => mergeTicket(prev, ticket));
				}
				break;
		}
	}, []);

	const { status: wsStatus } = useWebSocket(QUEUE_TOPIC, handleWsEvent);

	const prevStatusRef = useRef(wsStatus);
	useEffect(() => {
		const prev = prevStatusRef.current;
		prevStatusRef.current = wsStatus;
		if (prev === WS_STATUS.RECONNECTING && wsStatus === WS_STATUS.CONNECTED) {
			fetchAll();
		}
	}, [wsStatus, fetchAll]);

	// Безусловный ре-синк: даже если WS формально CONNECTED, но данные не идут
	// (half-open сокет), страница синхронизируется максимум раз в RESYNC_INTERVAL_MS.
	useEffect(() => {
		const id = setInterval(fetchAll, RESYNC_INTERVAL_MS);
		return () => clearInterval(id);
	}, [fetchAll]);

	// Мгновенный ре-синк при пробуждении вкладки / восстановлении сети
	useEffect(() => {
		const onVisible = () => {
			if (document.visibilityState === 'visible') fetchAll();
		};
		const onOnline = () => fetchAll();
		document.addEventListener('visibilitychange', onVisible);
		window.addEventListener('online', onOnline);
		return () => {
			document.removeEventListener('visibilitychange', onVisible);
			window.removeEventListener('online', onOnline);
		};
	}, [fetchAll]);

	return { tickets, loading, fetched, errorMessage, wsStatus };
};

// ─── Утилиты ─────────────────────────────────────────────────────────────────

/**
 * Обновляет существующий талон в списке или добавляет в начало если не найден.
 * CALLED-талоны должны быть видны первыми (менеджер уже ждёт клиента).
 */
function mergeTicket(prev, incoming) {
	const list = Array.isArray(prev) ? [...prev] : [];
	const idx = list.findIndex((t) => t.ticketNumber === incoming.ticketNumber);
	if (idx >= 0) {
		list[idx] = { ...list[idx], ...incoming };
		return list;
	}
	return [incoming, ...list];
}
